import { useEffect, useState } from "react";
import { Settings, Upload } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cardTonePill } from "@/lib/card-styles";
import { cn } from "@/lib/utils";

type DriverMode = "none" | "simulate";

export function SimulatedDriverSection({
  has_connector,
  loading,
  running,
  busy,
  error,
  signal_schema,
  unmapped_signals,
  on_enable_csv,
  on_disable,
  on_replace_csv,
}: {
  has_connector: boolean;
  loading: boolean;
  running: boolean;
  busy: boolean;
  error?: string | null;
  signal_schema?: Record<string, string>;
  unmapped_signals?: string[];
  on_enable_csv: (file: File) => Promise<void>;
  on_disable: () => Promise<void>;
  on_replace_csv: (file: File) => Promise<void>;
}) {
  const [mode, set_mode] = useState<DriverMode>(
    has_connector ? "simulate" : "none",
  );
  const [csv_open, set_csv_open] = useState(false);
  const [pending_file, set_pending_file] = useState<File | null>(null);
  const [dialog_error, set_dialog_error] = useState<string | null>(null);
  const [dragging, set_dragging] = useState(false);

  // Only force simulate when a connector appears — keep local "Simuloi" while waiting for Asetukset.
  useEffect(() => {
    if (has_connector) set_mode("simulate");
  }, [has_connector]);

  function open_csv_dialog() {
    set_pending_file(null);
    set_dialog_error(null);
    set_csv_open(true);
  }

  async function handle_driver_change(next: DriverMode) {
    if (next === mode || busy || loading) return;
    if (next === "simulate") {
      set_mode("simulate");
      return;
    }
    // next === "none" / Ei käytössä — tear down connector if present
    if (!has_connector) {
      set_mode("none");
      return;
    }
    try {
      await on_disable();
      set_mode("none");
    } catch {
      set_mode("simulate");
    }
  }

  async function confirm_csv() {
    if (!pending_file) {
      set_dialog_error("Valitse CSV-tiedosto.");
      return;
    }
    set_dialog_error(null);
    try {
      if (has_connector) {
        await on_replace_csv(pending_file);
      } else {
        await on_enable_csv(pending_file);
      }
      set_mode("simulate");
      set_csv_open(false);
      set_pending_file(null);
    } catch {
      set_dialog_error(
        has_connector
          ? "CSV:n vaihto epäonnistui."
          : "Simuloinnin käyttöönotto epäonnistui.",
      );
    }
  }

  function pick_csv(file: File | null) {
    if (!file) {
      set_pending_file(null);
      return;
    }
    if (!file.name.toLowerCase().endsWith(".csv")) {
      set_dialog_error("Tiedoston on oltava CSV.");
      return;
    }
    set_dialog_error(null);
    set_pending_file(file);
  }

  return (
    <div>
      <label className="mb-1 block text-xs text-muted-foreground">
        Laiteajuri
      </label>
      <div className="flex items-center gap-2">
        <select
          value={mode}
          disabled={busy || loading}
          onChange={(e) =>
            void handle_driver_change(e.target.value as DriverMode)
          }
          className="min-w-0 flex-1 rounded border border-border/40 bg-background px-2 py-1.5 text-sm outline-none focus:border-primary/60 disabled:opacity-50"
        >
          <option value="none">Ei käytössä</option>
          <option value="simulate">Simuloi</option>
        </select>
        {mode === "simulate" && (
          <button
            type="button"
            disabled={busy}
            onClick={(e) => {
              e.stopPropagation();
              open_csv_dialog();
            }}
            className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded border border-border/40 bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground disabled:opacity-50"
            title="Asetukset"
            aria-label="Asetukset"
          >
            <Settings className="h-4 w-4" />
          </button>
        )}
        {has_connector && (
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-0.5 text-[10.5px]",
              cardTonePill.learning,
            )}
          >
            Simuloitu
          </span>
        )}
      </div>
      {has_connector && (
        <p className="mt-1 text-xs text-muted-foreground">
          {running ? "Syöte käynnissä" : "Syöte pysäytetty"}
        </p>
      )}
      {has_connector && (
        <SignalMapSummary
          signal_schema={signal_schema}
          unmapped_signals={unmapped_signals}
        />
      )}
      {error && mode === "simulate" && (
        <p className="mt-1 text-[11px] text-destructive">{error}</p>
      )}

      <Dialog
        open={csv_open}
        onOpenChange={(open) => {
          set_csv_open(open);
          if (!open) {
            set_pending_file(null);
            set_dialog_error(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajurin asetukset</DialogTitle>
            <DialogDescription>
              Valitse tai pudota CSV-tiedosto, jota käytetään syötteen lähteenä.
            </DialogDescription>
          </DialogHeader>

          <label
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              set_dragging(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              set_dragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              set_dragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              set_dragging(false);
              pick_csv(e.dataTransfer.files?.[0] ?? null);
            }}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-8 text-xs text-muted-foreground transition",
              dragging
                ? "border-primary bg-primary/5 text-foreground"
                : "border-border hover:border-primary/40",
            )}
          >
            <Upload className="h-5 w-5" />
            {pending_file ? (
              <span className="max-w-full truncate text-foreground">
                {pending_file.name}
              </span>
            ) : (
              <span>Vedä CSV tähän tai klikkaa valitaksesi</span>
            )}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                pick_csv(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </label>

          {(dialog_error || error) && (
            <p className="text-xs text-destructive">{dialog_error ?? error}</p>
          )}

          <DialogFooter>
            <button
              type="button"
              disabled={busy}
              onClick={() => set_csv_open(false)}
              className="rounded px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              Peruuta
            </button>
            <button
              type="button"
              disabled={busy || !pending_file}
              onClick={() => void confirm_csv()}
              className="rounded bg-primary px-3 py-1.5 text-xs text-primary-foreground disabled:opacity-50"
            >
              {busy ? "Tallennetaan..." : "Käytä CSV:tä"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SignalMapSummary({
  signal_schema,
  unmapped_signals,
}: {
  signal_schema?: Record<string, string>;
  unmapped_signals?: string[];
}) {
  const mapped = Object.entries(signal_schema ?? {});
  const unmapped = unmapped_signals ?? [];
  if (mapped.length === 0 && unmapped.length === 0) return null;

  return (
    <div className="mt-2 space-y-1.5">
      {mapped.length > 0 && (
        <div>
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Kartoitus ({mapped.length})
          </div>
          <ul className="max-h-28 space-y-0.5 overflow-y-auto font-mono text-[10px] text-muted-foreground">
            {mapped.map(([source, target]) => (
              <li key={source} className="truncate">
                <span className="text-foreground">{source}</span>
                {" → "}
                {target}
              </li>
            ))}
          </ul>
        </div>
      )}
      {unmapped.length > 0 && (
        <div>
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Ei tunnistettu ({unmapped.length})
          </div>
          <p className="font-mono text-[10px] text-muted-foreground/70">
            {unmapped.join(", ")}
          </p>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        Data tulee läpi vasta kun kohdesignaali on avattu Signaalit-näkymässä.
      </p>
    </div>
  );
}
